/**
 * ONE ROLLOVER PLAN, AS MANY WORKDAYS AS THE WORK NEEDS.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 * Floyd's, 2026-09-29. A 1200-minute evaporator core (1500 minutes of Joe D's
 * OptiTime) was booked as an EOD split and persisted as TWO tickets:
 *
 *   Part A  Tue 10:11 → 22:20   (Tuesday closes 22:00)
 *   Part B  Wed 07:30 → 20:20   (Wednesday closes 16:00)
 *
 * smart-assign's rollover-only branch split the work with
 *
 *     day1Minutes = (shiftEnd + willingness) - earliestStart
 *     day2Minutes = optiTime - day1Minutes          ← everything else, one day
 *
 * so Part A was sized to 15 minutes PAST close, and Part B received every
 * remaining minute on a day that had 510 to give. A third workday never
 * existed. The cascade then computed each ticket's OptiFinish faithfully from
 * the (wrong) amount it was handed, and the cards told the truth about a plan
 * that was not.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE RULE
 * ═══════════════════════════════════════════════════════════════════════════
 * Consume the technician's valid work capacity one workday at a time until the
 * work is placed:
 *
 *   - a day is whatever `resolveDay` says it is — the caller answers from the
 *     canonical dated authorities (per-date hours, time off, holidays, breaks,
 *     partial-day absences); a null answer means "not a workday", and the plan
 *     moves to the next calendar date and asks again (never "+24h");
 *   - the first day starts at the requested start (or the open, if later);
 *     every later day starts at that day's open;
 *   - unavailable minutes (breaks, absences) are never capacity — they extend
 *     the wall-clock finish exactly as `calculateTicketOptiFinish` does;
 *   - a NON-FINAL segment finishes at the day's last working minute (its
 *     close, unless the day ends in an unavailable window);
 *   - the FINAL segment finishes when its work does.
 *
 * Units: `totalWorkMinutes` is technician OptiTime — execution minutes after
 * efficiency and buffer (canonicalTimeMath.calculateWallMinutes). Book minutes
 * are the caller's to convert. Minutes are integers from shop-local midnight;
 * dates are shop-local `yyyy-MM-dd`. No timezone arithmetic happens here.
 *
 * Pure and dependency-free on purpose: the Edge planner (smart-assign) and the
 * browser (OptiPlace / Catalyst re-planning a late start) run THIS code.
 */

export interface PlannerInterval {
  /** Minutes from shop-local midnight, inclusive. */
  start: number;
  /** Minutes from shop-local midnight, exclusive. */
  end: number;
}

/** One technician workday, as the canonical authorities describe it. */
export interface PlannerDay {
  dateStr: string;
  openMin: number;
  closeMin: number;
  /** Breaks, partial-day absences — any minute the technician cannot work. */
  unavailable: PlannerInterval[];
}

/** Null = not a workday for this technician (off, time off, holiday, closed). */
export type ResolveWorkDay = (dateStr: string) => PlannerDay | null;

export interface RolloverSegment {
  /** 1-based position in the chain: 1 = Part A. */
  sequence: number;
  dateStr: string;
  startMin: number;
  finishMin: number;
  /** OptiTime placed in this segment (never counts unavailable minutes). */
  workMinutes: number;
  /** OptiTime still unplaced after this segment. 0 on the final segment. */
  remainingAfterMinutes: number;
}

export interface RolloverPlan {
  totalWorkMinutes: number;
  segments: RolloverSegment[];
  /** False when the search horizon ran out before all work was placed. */
  complete: boolean;
  unplacedMinutes: number;
  /** Every workday the plan consulted, for a caller that must re-plan. */
  days: PlannerDay[];
}

/** How far the plan will look for workdays before it admits it cannot place. */
export const DEFAULT_ROLLOVER_SEARCH_DAYS = 60;

/** `yyyy-MM-dd` → the next calendar date. Pure; independent of any timezone. */
export function nextCalendarDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return next.toISOString().slice(0, 10);
}

/** Whole calendar days from `a` to `b` (both `yyyy-MM-dd`). */
export function calendarDaysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

function isFree(day: PlannerDay, minute: number): boolean {
  if (minute < day.openMin || minute >= day.closeMin) return false;
  return !day.unavailable.some((u) => minute >= u.start && minute < u.end);
}

/**
 * The day's normal-hours boundary: the end of its last working minute — its
 * close, unless the day ends in an unavailable window. A non-final segment
 * finishes here; so does Part A of an authorized live carry.
 */
export function lastWorkingMinute(day: PlannerDay): number {
  let last = day.openMin;
  for (let m = day.openMin; m < day.closeMin; m++) if (isFree(day, m)) last = m + 1;
  return last;
}

/** Working minutes in `[fromMin, day.closeMin)`. */
export function workCapacityFrom(day: PlannerDay, fromMin: number): number {
  let n = 0;
  for (let m = Math.max(fromMin, day.openMin); m < day.closeMin; m++) if (isFree(day, m)) n++;
  return n;
}

/**
 * Plan `totalWorkMinutes` of OptiTime from `startDateStr`/`startMin` across as
 * many workdays as it needs.
 */
export function planRolloverSegments(input: {
  totalWorkMinutes: number;
  startDateStr: string;
  startMin: number;
  resolveDay: ResolveWorkDay;
  maxCalendarDays?: number;
}): RolloverPlan {
  const total = Math.round(input.totalWorkMinutes);
  const horizon = input.maxCalendarDays ?? DEFAULT_ROLLOVER_SEARCH_DAYS;
  const segments: RolloverSegment[] = [];
  const days: PlannerDay[] = [];
  if (!Number.isFinite(total) || total <= 0) {
    return { totalWorkMinutes: 0, segments, complete: true, unplacedMinutes: 0, days };
  }

  let remaining = total;
  let dateStr = input.startDateStr;
  for (let offset = 0; offset < horizon && remaining > 0; offset++) {
    const day = input.resolveDay(dateStr);
    if (day && day.closeMin > day.openMin) {
      days.push(day);
      const from = offset === 0 ? Math.max(input.startMin, day.openMin) : day.openMin;
      let startMin = -1;
      let lastFreeEnd = -1;
      let placed = 0;
      for (let m = from; m < day.closeMin && placed < remaining; m++) {
        if (!isFree(day, m)) continue;
        if (startMin < 0) startMin = m;
        placed++;
        lastFreeEnd = m + 1;
      }
      if (placed > 0) {
        remaining -= placed;
        segments.push({
          sequence: segments.length + 1,
          dateStr,
          startMin,
          finishMin: lastFreeEnd,
          workMinutes: placed,
          remainingAfterMinutes: remaining,
        });
      }
    }
    dateStr = nextCalendarDate(dateStr);
  }

  return { totalWorkMinutes: total, segments, complete: remaining === 0, unplacedMinutes: remaining, days };
}

/**
 * The OptiFinish invariant, checked against the same day answers the plan
 * used. Returns every violation; an empty list is a valid plan.
 *
 *   non-final: finish <= the day's last working minute, and == it (the day is
 *              fully consumed from the segment's start);
 *   final:     finish == start + work + unavailable minutes crossed;
 *   every:     work > 0, starts on a working minute, sums to the total.
 */
export function rolloverPlanViolations(plan: RolloverPlan, resolveDay: ResolveWorkDay): string[] {
  const out: string[] = [];
  let sum = 0;
  plan.segments.forEach((s, i) => {
    const label = `segment ${s.sequence} (${s.dateStr})`;
    const day = resolveDay(s.dateStr);
    sum += s.workMinutes;
    if (!day) { out.push(`${label}: placed on a day that is not a workday`); return; }
    if (s.workMinutes <= 0) out.push(`${label}: non-positive work ${s.workMinutes}`);
    if (!isFree(day, s.startMin)) out.push(`${label}: starts on a non-working minute ${s.startMin}`);
    // Finish = the minute after the last worked minute, walking free minutes.
    let placed = 0; let finish = s.startMin;
    for (let m = s.startMin; m < day.closeMin && placed < s.workMinutes; m++) {
      if (isFree(day, m)) { placed++; finish = m + 1; }
    }
    if (placed < s.workMinutes) out.push(`${label}: ${s.workMinutes} min does not fit before close ${day.closeMin}`);
    if (finish !== s.finishMin) out.push(`${label}: finish ${s.finishMin} != calculated ${finish}`);
    const isFinal = i === plan.segments.length - 1;
    if (!isFinal) {
      const lastWorking = lastWorkingMinute(day);
      if (s.finishMin > lastWorking) out.push(`${label}: non-final finish ${s.finishMin} past the day's boundary ${lastWorking}`);
      if (s.finishMin !== lastWorking) out.push(`${label}: non-final segment leaves capacity unused (finish ${s.finishMin}, boundary ${lastWorking})`);
    } else if (plan.complete && s.remainingAfterMinutes !== 0) {
      out.push(`${label}: final segment leaves ${s.remainingAfterMinutes} min`);
    }
    if (i > 0 && s.dateStr <= plan.segments[i - 1].dateStr) out.push(`${label}: not after the previous segment's day`);
  });
  if (plan.complete && sum !== plan.totalWorkMinutes) out.push(`work ${sum} != total ${plan.totalWorkMinutes}`);
  return out;
}

/**
 * Book minutes for each segment, conserving the ticket's book total.
 *
 * Non-final segments take `floor` of their OptiTime converted back to book
 * minutes, so their recomputed OptiFinish can never pass the day's boundary.
 * The final segment takes the exact remainder, so the chain never loses (or
 * invents) catalogued work to rounding.
 */
export function segmentBookMinutes(
  workMinutes: number[],
  totalBookMinutes: number,
  efficiencyPct: number,
  bufferPct: number,
): number[] {
  const eff = Math.max(efficiencyPct, 1) / 100;
  const buf = 1 + bufferPct / 100;
  const total = Math.round(totalBookMinutes);
  const out: number[] = [];
  let used = 0;
  workMinutes.forEach((w, i) => {
    if (i < workMinutes.length - 1) {
      const b = Math.max(1, Math.floor((w * eff) / buf));
      out.push(b);
      used += b;
    } else {
      out.push(total - used);
    }
  });
  // W4 (field test 2026-10-16): the final part used to be clamped to max(1, …), which INVENTED book
  // minutes whenever the non-final floors already reached the total (Σ > total). Re-floor instead:
  // take single minutes from the largest non-final parts until the final part holds at least 1, so
  // Σ == total holds whenever total >= the number of parts.
  const last = out.length - 1;
  while (last >= 0 && out[last] < 1) {
    let j = -1;
    for (let k = 0; k < last; k++) if (out[k] > 1 && (j < 0 || out[k] > out[j])) j = k;
    if (j < 0) { out[last] = Math.max(1, out[last]); break; } // total < parts: cannot conserve
    out[j]--;
    out[last]++;
  }
  return out;
}
